import { UserStatus, UserStatusInfo } from '../../model/user-status-info.model';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { withTreeShakableDevTools } from './userStore.devtools.feature';
import { inject } from '@angular/core';
import { UserService } from '../../service/user/user.service';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { UserData } from '../../model/user-data.model';
import { HttpErrorResponse } from '@angular/common/http';
import { Permission } from '../../model/permission.model';
import { toObservable } from '@angular/core/rxjs-interop';

interface UserState {
  userData: UserData;
  userStatusInfo: UserStatusInfo;
  isInitialized: boolean;
  loadingUserData: boolean;
  loadingStatus: boolean;
  loadingRegistration: boolean;
  errorUserdata: HttpErrorResponse;
  errorUserStatus: HttpErrorResponse;
  errorRegistration: HttpErrorResponse;
}

const initialState: UserState = {
  userData: { id: null, keycloakId: null, firstName: null, lastName: null, email: null, role: null, permissions: [], gpNrs: [] },
  userStatusInfo: { numberOfVerificationAttempts: null, registeredAt: null, status: null },
  isInitialized: false,
  loadingUserData: false,
  loadingStatus: false,
  loadingRegistration: false,
  errorUserdata: null,
  errorUserStatus: null,
  errorRegistration: null,
};

export const UserStore = signalStore(
  { providedIn: 'root' },
  withTreeShakableDevTools('UserStore'),
  withState(initialState),
  withMethods((store, userService = inject(UserService)) => {
    return {
      async loadUserdata(): Promise<void> {
        patchState(store, { loadingUserData: true, errorUserdata: null });
        try {
          const response: UserData = await lastValueFrom(userService.getUserDataObservable());
          patchState(store, { userData: response });
        } catch (error) {
          patchState(store, { errorUserdata: error });
        }
        patchState(store, { loadingUserData: false });
      },

      async loadUserStatus(): Promise<void> {
        patchState(store, { loadingStatus: true, errorUserStatus: null });
        try {
          const response: UserStatusInfo = await lastValueFrom(userService.getUserStatus());
          patchState(store, {
            userStatusInfo: response,
          });
        } catch (error) {
          patchState(store, { errorUserStatus: error });
        }
        patchState(store, { loadingStatus: false });
        if (store.userStatusInfo.status() === UserStatus.ACTIVE) {
          await this.loadUserdata();
        }
      },

      async registerUser(): Promise<void> {
        patchState(store, { loadingRegistration: true, errorRegistration: null });
        try {
          await firstValueFrom(userService.registerUser());
          await this.loadUserStatus();
          patchState(store, { loadingRegistration: false });
        } catch (error) {
          patchState(store, { errorRegistration: error, loadingRegistration: false });
        }
      },
    };
  }),
  withHooks({
    async onInit(store) {
      await store.loadUserStatus();
      patchState(store, { isInitialized: true });
    },
  }),
  withComputed(store => ({
    isAdmin: () => store.userData.permissions().includes(Permission.ADMIN),
    isActive: () => store.userStatusInfo.status() === UserStatus.ACTIVE,
  })),
  withProps(({ isInitialized }) => ({
    isInitialized$: toObservable(isInitialized),
  })),
);
